import { AutocompleteProps, TextField, TextFieldProps } from '@mui/material';
import { SyntheticEvent } from 'react';
import { Controller } from 'react-hook-form';
import MBAutocomplete from '../mbui/MBAutocomplete';

interface Props extends Partial<IProps<any, any, any, any>> {
  name: string;
}

interface IOptionProps {
  value: string | number | null;
  label: string;
}

type IProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  'renderInput'
> & {
  options: IOptionProps[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  fullWidth?: boolean;
  textFieldProps?: TextFieldProps;
  renderInput?: AutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo
  >['renderInput'];
};

const RHFAutoComplete = ({ name, options = [], ...others }: Props) => {
  const {
    textFieldProps,
    label,
    placeholder,
    required,
    fullWidth,
    renderInput: renderInputProp,
    ...autocompleteProps
  } = others;

  return (
    <Controller
      name={name}
      render={({ field: { onChange, ...field }, fieldState: { error } }) => {
        const onSelectChange = (
          _event: SyntheticEvent<Element, Event>,
          value: unknown,
          reason: string
        ) => {
          const selectedOption = value as IOptionProps | null;

          if (reason === 'clear') {
            onChange(null);
            return;
          }
          onChange(selectedOption?.value);
        };

        const originalRenderInput = renderInputProp as
          | ((params: any) => JSX.Element)
          | undefined;

        return (
          <MBAutocomplete
            {...field}
            value={options.find((op) => op.value === field.value) || null}
            getOptionLabel={(option) =>
              (option as IOptionProps).label
                ? (option as IOptionProps).label
                : ''
            }
            isOptionEqualToValue={(option, value) =>
              (option as IOptionProps)?.value === (value as IOptionProps)?.value
            }
            options={options}
            onChange={onSelectChange}
            renderInput={
              originalRenderInput
                ? (params) =>
                    originalRenderInput({
                      ...(params as any),
                      error: !!error,
                      helperText: error ? error.message : '',
                    })
                : (params) => (
                    <TextField
                      {...params}
                      fullWidth={fullWidth ?? true}
                      placeholder={placeholder}
                      label={label}
                      required={required}
                      error={!!error}
                      helperText={error ? error.message : ''}
                      {...(textFieldProps || {})}
                    />
                  )
            }
            {...autocompleteProps}
          />
        );
      }}
    />
  );
};

export default RHFAutoComplete;
